// Frontend-only stub data for Phase B of the v2 /app experience.
// These replace backend data while the UI is under design review. Each block
// has a TODO comment pointing at the endpoint that will eventually replace it.

export type StubInvoice = {
  nr: string;
  date: string; // human-readable German date
  iso: string; // ISO date string for sorting / filtering
  year: number;
  title: string;
  sub: string;
  total: string; // "126,65 €"
  status: "paid" | "open";
  promoCode?: string;
  // Line items used in the invoice preview card on /app/invoices.
  items: {
    netSubtotal: string; // "106,43 €"
    vat: string; // "20,22 €"
    paymentNote: string; // "Mastercard •••• 4242"
  };
};

// TODO(Phase C): replace with GET /me/invoices (Stripe-backed).
export const STUB_INVOICES: StubInvoice[] = [
  {
    nr: "R-2026-0412",
    date: "7. April 2026",
    iso: "2026-04-07",
    year: 2026,
    title: "Vitanara · Basis-Paket",
    sub: "für Hildegard Wagner",
    total: "126,65 €",
    status: "paid",
    promoCode: "OMA2026",
    items: {
      netSubtotal: "106,43 €",
      vat: "20,22 €",
      paymentNote: "Mastercard •••• 4242",
    },
  },
  {
    nr: "R-2026-0411",
    date: "7. April 2026",
    iso: "2026-04-07",
    year: 2026,
    title: "Begrüßungskarte · Versand",
    sub: "an Hildegard Wagner",
    total: "0,00 €",
    status: "paid",
    items: {
      netSubtotal: "0,00 €",
      vat: "0,00 €",
      paymentNote: "Mastercard •••• 4242",
    },
  },
  {
    nr: "R-2026-0398",
    date: "2. April 2026",
    iso: "2026-04-02",
    year: 2026,
    title: "Reservierungsbestätigung",
    sub: "Vorab-Buchung Basis",
    total: "0,00 €",
    status: "paid",
    items: {
      netSubtotal: "0,00 €",
      vat: "0,00 €",
      paymentNote: "Mastercard •••• 4242",
    },
  },
  {
    nr: "R-2025-1207",
    date: "12. Dezember 2025",
    iso: "2025-12-12",
    year: 2025,
    title: "Vitanara · Beta-Vorbestellung",
    sub: "für Hildegard Wagner",
    total: "29,00 €",
    status: "paid",
    items: {
      netSubtotal: "24,37 €",
      vat: "4,63 €",
      paymentNote: "Mastercard •••• 4242",
    },
  },
];

export type StubFamilyMember = {
  id: string;
  name: string;
  role: string; // "Bruder", "Schwiegertochter", …
  tag: "Vollzugriff" | "Lesen + Hören" | "Lesen" | "Eingeladen" | "—";
  you?: boolean;
  pending?: boolean;
  isStoryteller?: boolean;
};

// TODO(Phase C): replace with GET /me/family.
export const STUB_FAMILY_MEMBERS: StubFamilyMember[] = [
  {
    id: "self",
    name: "Marie Wagner",
    role: "Inhaberin",
    tag: "Vollzugriff",
    you: true,
  },
  {
    id: "jonas",
    name: "Jonas Wagner",
    role: "Bruder",
    tag: "Lesen + Hören",
  },
  {
    id: "anna",
    name: "Anna Bürger",
    role: "Schwiegertochter",
    tag: "Eingeladen",
    pending: true,
  },
];

export type StubPhoto = {
  id: string;
  chapter: string; // Roman numeral
  caption: string; // year or short caption
  empty?: boolean;
};

// TODO(Phase C): replace with GET /me/photos and POST upload endpoint.
export const STUB_PHOTOS: StubPhoto[] = [
  { id: "p1", chapter: "I", caption: "1942" },
  { id: "p2", chapter: "II", caption: "1944" },
  { id: "p3", chapter: "III", caption: "1956" },
  { id: "p4", chapter: "IV", caption: "1957" },
  { id: "p5", chapter: "IV", caption: "1958" },
  { id: "p6", chapter: "V", caption: "1960" },
  { id: "p7", chapter: "VI", caption: "1965" },
  { id: "e1", chapter: "", caption: "", empty: true },
  { id: "e2", chapter: "", caption: "", empty: true },
  { id: "e3", chapter: "", caption: "", empty: true },
];

// Stub book metadata for /app/book — TODO(Phase C): GET /me/book.
export const STUB_BOOK = {
  filename: "Hildegard-Wagner_Band-I.pdf",
  pages: 184,
  photos: 12,
  format: "A5 · 21 × 14,8 cm",
  size: "46,2 MB",
  versions: [
    { id: "v3", label: "Endfassung", date: "12. Mai 2026", note: "nach Lektorat", current: true },
    { id: "v2", label: "v2", date: "28. April 2026", note: "11 Kapitel" },
    { id: "v1", label: "v1", date: "12. April 2026", note: "7 Kapitel" },
  ],
};

export type StubPostalCardTimelineState = "done" | "active" | "pending";
export type StubPostalCardTimelineKey =
  | "ordered"
  | "written"
  | "shipped"
  | "transit"
  | "delivered";

export type StubPostalCard = {
  recipient: string;
  recipient_address: string;
  phone_on_card: string;
  status: "bestellt" | "geschrieben" | "eingeliefert" | "in_transit" | "zugestellt";
  ordered_at: string;
  expected_at: string;
  timeline: {
    key: StubPostalCardTimelineKey;
    label: string;
    at: string | null;
    state: StubPostalCardTimelineState;
  }[];
};

// TODO(Phase C): replace with GET /me/postal-card.
export const STUB_POSTAL_CARD: StubPostalCard = {
  recipient: "Hildegard Kreuzer",
  recipient_address: "Lindenweg 14, 24340 Eckernförde",
  phone_on_card: "+49 4103 70 44 937",
  status: "in_transit",
  ordered_at: "2026-05-08T10:14:00Z",
  expected_at: "2026-05-15T00:00:00Z",
  timeline: [
    { key: "ordered", label: "Bestellung aufgegeben", at: "2026-05-08T10:14Z", state: "done" },
    { key: "written", label: "Karte geschrieben", at: "2026-05-09T16:30Z", state: "done" },
    { key: "shipped", label: "Bei DHL eingeliefert", at: "2026-05-10T08:00Z", state: "done" },
    { key: "transit", label: "In Zustellung", at: null, state: "active" },
    { key: "delivered", label: "Zugestellt", at: null, state: "pending" },
  ],
};

// PEALO / billing letterhead values rendered on the invoice preview card.
// Pulled in-line so it stays in sync with /impressum.
export const BILLING_LETTERHEAD = {
  company: "tino24/7 GmbH",
  street: "Ringbahnstr. 42",
  cityZip: "12099 Berlin",
  hrb: "HRB 256376 B",
  ustId: "USt-ID DE365576955",
};
